import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { submitApplication } from '@api/application'
import { useLang, withLang } from '@shared/i18n/useLang'

export interface ApplicationFormProps {
  /** Fixed direction name (e.g. current DirectionPage title). When set, the manual selector is hidden. */
  direction?: string
}

function Field({ name, label, type, placeholder, required }: { name: string; label: string; type: string; placeholder?: string; required?: boolean }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#5C6670' }}>{label}{required && ' *'}</span>
      <input name={name} type={type} required={required} placeholder={placeholder} className="input-field" style={{ padding: '12px 14px' }} />
    </label>
  )
}

export function ApplicationForm({ direction }: ApplicationFormProps) {
  const { t } = useTranslation()
  const lang = useLang()
  const kinds = t('applicationForm.kinds', { returnObjects: true }) as string[]
  const [kind, setKind] = useState(kinds[0])
  const [sent, setSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)
    const fileInput = form.elements.namedItem('documents') as HTMLInputElement | null

    setSubmitting(true)
    setError(null)
    try {
      await submitApplication({
        direction: direction ?? kind,
        name: String(data.get('name') ?? ''),
        company: String(data.get('company') ?? ''),
        phone: String(data.get('phone') ?? ''),
        email: String(data.get('email') ?? ''),
        message: String(data.get('message') ?? ''),
        agree: data.get('agree') === 'on',
        files: fileInput?.files ?? null,
        pageUrl: window.location.href,
      })
      setSent(true)
      form.reset()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('applicationForm.errorGeneric'))
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <div className="card" style={{ padding: '64px 48px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
        <span style={{ width: 48, height: 48, background: '#171C22', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#29ABE2', fontSize: 22, fontWeight: 700 }}>✓</span>
        <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#171C22' }}>{t('applicationForm.sentTitle')}</h2>
        <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: '#5C6670', maxWidth: 380 }}>{t('applicationForm.sentText')}</p>
        <button
          onClick={() => setSent(false)}
          style={{ marginTop: 8, border: '1px solid #C6CDD3', background: 'none', color: '#171C22', fontSize: 12, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', padding: '12px 24px', borderRadius: 3, cursor: 'pointer', transition: 'border-color .15s,color .15s' }}
        >
          {t('applicationForm.sendAnother')}
        </button>
      </div>
    )
  }

  return (
    <form className="card" style={{ padding: 40, display: 'flex', flexDirection: 'column', gap: 24 }} onSubmit={handleSubmit}>
      {!direction && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: '#5C6670' }}>{t('applicationForm.directionLabel')}</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {kinds.map(k => (
                <button
                  key={k} type="button" onClick={() => setKind(k)}
                  style={{
                    fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '10px 18px', borderRadius: 3, cursor: 'pointer', transition: 'all .15s',
                    background: k === kind ? '#171C22' : '#FFFFFF',
                    color: k === kind ? '#FFFFFF' : '#5C6670',
                    border: `1px solid ${k === kind ? '#171C22' : '#C6CDD3'}`,
                  }}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div style={{ height: 1, background: '#EEF1F3' }} />
        </>
      )}

      <div className="grid-2">
        <Field name="name" label={t('applicationForm.nameLabel')} type="text" placeholder={t('applicationForm.namePlaceholder')} required />
        <Field name="company" label={t('applicationForm.companyLabel')} type="text" placeholder={t('applicationForm.companyPlaceholder')} />
      </div>
      <div className="grid-2">
        <Field name="phone" label={t('applicationForm.phoneLabel')} type="tel" placeholder="+7 (XXX) XXX-XX-XX" required />
        <Field name="email" label={t('applicationForm.emailLabel')} type="email" placeholder="email@example.com" required />
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#5C6670' }}>{t('applicationForm.messageLabel')}</span>
        <textarea
          name="message" required rows={6}
          placeholder={t('applicationForm.messagePlaceholder')}
          className="input-field"
          style={{ resize: 'vertical', lineHeight: 1.55 }}
        />
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#5C6670' }}>{t('applicationForm.documentsLabel')}</span>
        <span style={{ border: '1px dashed #C6CDD3', borderRadius: 3, padding: '20px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, cursor: 'pointer', transition: 'border-color .15s' }}>
          <span style={{ width: 6, height: 6, background: '#189BD5' }} />
          <span style={{ fontSize: 13, color: '#5C6670' }}>{t('applicationForm.documentsHint')}</span>
          <input name="documents" type="file" multiple style={{ display: 'none' }} />
        </span>
      </label>

      {error && <p style={{ margin: 0, fontSize: 13, color: '#C0392B' }}>{error}</p>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input name="agree" type="checkbox" required style={{ marginTop: 3, accentColor: '#189BD5' }} />
          <span style={{ fontSize: 12.5, lineHeight: 1.5, color: '#93A3AE', maxWidth: 340 }}>
            {t('applicationForm.agreeText1')}
            <Link
              to={withLang(lang, '/consent')}
              target="_blank"
              rel="noopener"
              style={{ color: '#93A3AE', textDecoration: 'underline', textUnderlineOffset: 2 }}
            >
              {t('applicationForm.agreeConsentLink')}
            </Link>
            {t('applicationForm.agreeText2')}
            <Link
              to={withLang(lang, '/privacy')}
              target="_blank"
              rel="noopener"
              style={{ color: '#93A3AE', textDecoration: 'underline', textUnderlineOffset: 2 }}
            >
              {t('applicationForm.agreePolicyLink')}
            </Link>
          </span>
        </label>
        <button
          type="submit"
          disabled={submitting}
          style={{ flexShrink: 0, background: '#189BD5', color: '#FFFFFF', fontSize: 13, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', padding: '15px 32px', border: 'none', borderRadius: 3, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? .7 : 1, transition: 'background .15s' }}
        >
          {submitting ? t('applicationForm.submitting') : t('applicationForm.submit')}
        </button>
      </div>
    </form>
  )
}
